import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { client } from "@/api"

interface GamesQueryParams {
  page?: number
  pageSize?: number
  search?: string
  sortBy?: "created_at" | "title" | "when"
  sortOrder?: "asc" | "desc"
}

export interface AdminGame {
  public_id: string
  title: string | null
  when: string
  type: string
  cover_image: string | null
  game_meta: Record<string, unknown>
  created_at: string
  creator_name: string | null
  creator_email: string | null
  creator_public_id: string | null
  team_name: string | null
  team_public_id: string | null
  clips_count: number
  total_size: number
  tagged_count: number
  predicted_count: number
}

interface GamesResponse {
  games: AdminGame[]
  pagination: {
    page: number
    pageSize: number
    totalCount: number
    totalPages: number
    hasNextPage: boolean
    hasPreviousPage: boolean
  }
}

export function useGames(params: GamesQueryParams = {}) {
  const {
    page = 1,
    pageSize = 20,
    search = "",
    sortBy = "created_at",
    sortOrder = "desc",
  } = params

  return useQuery<GamesResponse>({
    queryKey: ["games", page, pageSize, search, sortBy, sortOrder],
    // Keep the previous page/search results on screen while the next query loads,
    // so paginating or typing in the search box doesn't flash a skeleton (which
    // would unmount the search input and steal focus mid-type).
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const response = await client.admin.games.$get({
        query: {
          page,
          pageSize,
          search: search || undefined,
          sortBy,
          sortOrder,
        },
      })

      if (!response.ok) {
        throw new Error("Failed to fetch games")
      }

      return response.json() as Promise<GamesResponse>
    },
  })
}

export interface AdminClip {
  public_id: string
  video_url: string
  lexorank: string
  size: number | null
  data: Record<string, unknown>
  predicted: Record<string, unknown>
  video_meta: Record<string, unknown>
  cover_image: string | null
  created_at: string
}

interface GameDetailResponse {
  game: {
    public_id: string
    title: string | null
    when: string
    type: string
    uniforms: string | null
    cover_image: string | null
    game_meta: Record<string, unknown>
    created_at: string
    creator_name: string | null
    creator_email: string | null
    creator_public_id: string | null
    team_name: string | null
    team_public_id: string | null
  }
  clips: AdminClip[]
  stats: {
    clips_count: number
    total_size: number
    tagged_count: number
    predicted_count: number
  }
}

export function useGameDetail(publicId: string) {
  return useQuery<GameDetailResponse>({
    queryKey: ["games", publicId],
    queryFn: async () => {
      const response = await client.admin.games[":publicId"].$get({
        param: { publicId },
      })

      if (!response.ok) {
        throw new Error("Failed to fetch game details")
      }

      return response.json() as Promise<GameDetailResponse>
    },
    enabled: !!publicId,
  })
}

// A soft-deleted clip in the 30-day archive window, with the position Restore
// would drop it back into and whether it's a virtual slice or a whole-file clip.
export interface AdminDeletedClip {
  public_id: string
  video_url: string
  lexorank: string
  size: number | null
  start_seconds: number | null
  end_seconds: number | null
  video_meta: Record<string, unknown>
  cover_image: string | null
  created_at: string
  sliced_at: string | null
  deleted_at: string
  // 0-based index among the current active set; the clip becomes the
  // (restore_position + 1)th clip after restore.
  restore_position: number
  is_virtual: boolean
  // Length in seconds (virtual slice window, or whole-file source duration);
  // null when the source duration is unknown. Computed server-side.
  length_seconds: number | null
}

interface DeletedClipsResponse {
  clips: AdminDeletedClip[]
  active_count: number
  // True when the archive had more rows than the server cap and older ones were dropped.
  truncated: boolean
}

export function useDeletedClips(publicId: string) {
  return useQuery<DeletedClipsResponse>({
    queryKey: ["games", publicId, "deleted-clips"],
    queryFn: async () => {
      const response = await client.admin.games[":publicId"]["deleted-clips"].$get({
        param: { publicId },
      })

      if (!response.ok) {
        throw new Error("Failed to fetch deleted clips")
      }

      return response.json() as Promise<DeletedClipsResponse>
    },
    enabled: !!publicId,
  })
}

// One distinct whole-file source's outcome from a shrink run.
export interface ShrinkSourceResult {
  clip_public_id: string
  video_url: string
  action: "enqueued" | "skipped"
  // Present when action === "skipped": already-small | transcode-running |
  // original-missing | original-key-unknown | duplicate-source.
  reason?: string
}

export interface ShrinkGameResponse {
  game_public_id: string
  force: boolean
  force_shrink: boolean
  bitrate_ceiling_mbps: number
  enqueued: number
  sources: ShrinkSourceResult[]
}

// Retroactively shrink a game's source videos (#978). force bypasses the already-small
// guard + the worker idempotency skip; force_shrink additionally ignores the worker's
// savings guardrail (re-encode even an already-deliverable file); bitrate_ceiling_mbps
// overrides the default ceiling.
export function useShrinkGame(gamePublicId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    ShrinkGameResponse,
    Error,
    { force?: boolean; force_shrink?: boolean; bitrate_ceiling_mbps?: number }
  >({
    mutationFn: async ({ force, force_shrink, bitrate_ceiling_mbps }) => {
      const response = await client.admin.games[":publicId"].shrink.$post({
        param: { publicId: gamePublicId },
        json: {
          force: force ?? false,
          force_shrink: force_shrink ?? false,
          ...(bitrate_ceiling_mbps != null ? { bitrate_ceiling_mbps } : {}),
        },
      })
      if (!response.ok) {
        let message = `Shrink failed (${response.status})`
        try {
          const body = (await response.json()) as { message?: string }
          if (body?.message) message = body.message
        } catch {
          // non-JSON body — keep the generic message
        }
        throw new Error(message)
      }
      return response.json() as Promise<ShrinkGameResponse>
    },
    onSuccess: () => {
      // Enqueued sources flip transcode_status -> running; refetch the game detail.
      queryClient.invalidateQueries({ queryKey: ["games", gamePublicId] })
    },
  })
}

// One distinct transcoded source's outcome from an "add missing renditions" run.
export interface RenderLadderSourceResult {
  source_key: string
  action: "dispatched" | "skipped"
  // Present when action === "dispatched".
  labels?: string[]
  progressive?: boolean
  // Present when action === "skipped": absent | etag_mismatch | inflight | nothing_missing |
  // not_worth_it | error.
  reason?: string
}

export interface RenderLadderResponse {
  game_public_id: string
  dispatched: number
  // The request walks at most a page of the game's sources; when true, send `next_cursor`
  // back as `after` to continue where this page stopped.
  truncated: boolean
  next_cursor: string | null
  sources: RenderLadderSourceResult[]
}

// Add the missing lower-bitrate renditions for a game's sources (#1578). Publish-once: a
// source that already has its rung for the current bytes is a no-op; nothing is replaced.
export function useRenderLadder(gamePublicId: string) {
  return useMutation<RenderLadderResponse, Error, { after?: string }>({
    mutationFn: async ({ after }) => {
      const response = await client.admin.games[":publicId"]["render-ladder"].$post({
        param: { publicId: gamePublicId },
        json: after ? { after } : {},
      })
      if (!response.ok) {
        let message = `Add missing renditions failed (${response.status})`
        try {
          const body = (await response.json()) as { message?: string }
          if (body?.message) message = body.message
        } catch {
          // non-JSON body — keep the generic message
        }
        throw new Error(message)
      }
      return response.json() as Promise<RenderLadderResponse>
    },
  })
}

// Permanently delete a game (and its clips). Reuses the user-facing
// `DELETE /v1/games/delete` endpoint, which already authorizes superusers — and
// every admin-app user is a superuser, so the JWT cookie carries that identity.
// Used for occasional ops requests and DMCA takedowns. The backend frees the R2
// objects asynchronously after responding, and blocks deletion (409) if the game
// is mid-exchange.
export function useDeleteGame() {
  const queryClient = useQueryClient()
  return useMutation<{ message: string }, Error, { publicId: string }>({
    mutationFn: async ({ publicId }) => {
      const response = await client.v1.games.delete.$delete({
        query: { gameId: publicId },
      })
      if (!response.ok) {
        let message = `Delete failed (${response.status})`
        try {
          const body = (await response.json()) as { message?: string }
          if (body?.message) message = body.message
        } catch {
          // non-JSON body — keep the generic message
        }
        throw new Error(message)
      }
      return response.json() as Promise<{ message: string }>
    },
    onSuccess: () => {
      // The game is gone from the list; drop it from any cached games queries.
      queryClient.invalidateQueries({ queryKey: ["games"] })
    },
  })
}

export function useUpdateGameDate(gamePublicId: string) {
  const queryClient = useQueryClient()
  return useMutation<
    { public_id: string; when: string },
    Error,
    { when: string }
  >({
    mutationFn: async ({ when }) => {
      const response = await client.admin.games[":publicId"].when.$patch({
        param: { publicId: gamePublicId },
        json: { when },
      })
      if (!response.ok) {
        let message = `Update failed (${response.status})`
        try {
          const body = (await response.json()) as { message?: string }
          if (body?.message) message = body.message
        } catch {
          // non-JSON body — keep the generic message
        }
        throw new Error(message)
      }
      return response.json() as Promise<{ public_id: string; when: string }>
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["games", gamePublicId] })
    },
  })
}

export function useRestoreClip(gamePublicId: string) {
  const queryClient = useQueryClient()
  return useMutation<{ status: string; public_id: string }, Error, { publicId: string }>({
    mutationFn: async ({ publicId }) => {
      const response = await client.admin.clips[":publicId"].restore.$post({
        param: { publicId },
      })
      if (!response.ok) {
        // Surface the backend's 409 message (e.g. slot taken / time-window covered).
        let message = `Restore failed (${response.status})`
        try {
          const body = (await response.json()) as { message?: string }
          if (body?.message) message = body.message
        } catch {
          // non-JSON body — keep the generic message
        }
        throw new Error(message)
      }
      return response.json() as Promise<{ status: string; public_id: string }>
    },
    onSuccess: () => {
      // The clip moved from the deleted list back into the active set.
      queryClient.invalidateQueries({ queryKey: ["games", gamePublicId, "deleted-clips"] })
      queryClient.invalidateQueries({ queryKey: ["games", gamePublicId] })
    },
  })
}
